import {Component, EventEmitter, Input, Output, OnInit} from "@angular/core";
import {Router} from "@angular/router";
import "../../../assets/libs/ckeditor5/build/translations/es";
import "../../../assets/libs/ckeditor5/build/translations/fr";
import "../../../assets/libs/ckeditor5/build/translations/ar";
import "../../../assets/libs/ckeditor5/build/translations/et";
import {ClassicEditor} from "../../../assets/libs/ckeditor5";
import {ToastrService} from "ngx-toastr";
import {Store} from "../../services/store.service";
import {BaseError} from "../pages/base-error";
import {ResizeEvent} from "angular-resizable-element";
import {AuthService} from "../../services/auth/auth-service";
import { LanguageControllerService } from "src/app/services/language-controller/language-controller.service";
import { TranslateService } from "@ngx-translate/core";
import { Language } from "src/app/models/language";
import {environment} from "../../../environments/environment";

@Component({
  selector: "tutorial-layer",
  templateUrl: "./tutorial-layer.component.html",
  providers: [],
  styles: [],
})

export class TutorialLayerComponent extends BaseError implements OnInit {

  private languages: Language[];
  public langIsRTL: boolean = false;
  public position: string = localStorage.getItem("drawer-position") || "right";

  constructor(public toastr: ToastrService,
              private router: Router,
              private authService: AuthService,
              public store: Store,
              public translate: TranslateService,
              private languageService: LanguageControllerService) {
      super(toastr);
      languageService.getLanguages().subscribe(
        (data) => {
          this.languages = data;
          translate.addLangs(data.map((l) => l.code));
          this.checkRTL();
        },
      );
  }

  @Input() public editorData: {[key: string]: string};
  @Input() public title: string;
  @Input() public isClosed: boolean = true;
  @Input() public selectedTab: string = "tutorial";
  @Input() public selectedData: any = "";
  @Output() public saveTutorial = new EventEmitter();
  @Output() public closingTutorial = new EventEmitter();
  @Input() public userName: string = "";
  @Input() public forceTooltipDir: string = null;
  @Input() public showLabel: boolean = false;
  @Input() public hideTrigger: boolean = false;
  public readOnly: boolean = false;
  public lvl: number = 1;

  public initialTab: string;

  public Editor = ClassicEditor;
  public config = {
    toolbar: {
      items: [
        "heading",
        "|",
        "fontFamily",
        "fontSize",
        "fontColor",
        "fontBackgroundColor",
        "underline",
        "bold",
        "italic",
        "link",
        "bulletedList",
        "numberedList",
        "|",
        "alignment",
        "outdent",
        "indent",
        "|",
        "imageUpload",
        "blockQuote",
        "insertTable",
        // "mediaEmbed",
        "undo",
        "redo",
        "|",
        "horizontalLine",
        "|",
        "removeFormat",
      ],
    },
    language: this.store.state.common.lang,
    image: {
      toolbar: [
        "imageTextAlternative",
        "imageStyle:full",
        "imageStyle:side",
        "linkImage",
      ],
    },
    table: {
      contentToolbar: [
        "tableColumn",
        "tableRow",
        "mergeTableCells",
        "tableCellProperties",
        "tableProperties",
      ],
    },
  };
  public resizeEdges = { bottom: false, right: false, top: false, left: true };
  public style: object = {};

  public toggleTutorial(): void {
    if (this.isClosed) {
      this.selectedTab = this.initialTab;
    }
    this.isClosed = !this.isClosed;
    this.checkMissingLanguage();
    this.selectedData = this.editorData[this.store.state.common.lang];
  }

  public changePosition(position: string) {
    this.style = {};
    this.position = position;
    localStorage.setItem("drawer-position", position);
    switch (position) {
      case "top":
        this.resizeEdges = { bottom: true, right: false, top: false, left: false };
        break;
      case "right":
        this.resizeEdges = { bottom: false, right: false, top: false, left: true };
        break;
      case "bottom":
        this.resizeEdges = { bottom: false, right: false, top: true, left: false };
        break;
      case "left":
        this.resizeEdges = { bottom: false, right: true, top: false, left: false };
        break;
    }
  }

  private checkRTL() {
    const currentLangCode = this.translate.currentLang || this.translate.getDefaultLang();
    const currentLanguage = this.languages.find((l) => l.code === currentLangCode);
    this.langIsRTL = currentLanguage && currentLanguage.rtllanguage;
  }

  public checkMissingLanguage() {
    if (!this.editorData) {
      this.editorData = {};
    }
    if (!this.editorData[this.store.state.common.lang]) {
      this.editorData[this.store.state.common.lang] = this.editorData[environment.DEFAULT_LANGUAGE]
        ? this.translate.instant("From") + " (" + environment.DEFAULT_LANGUAGE + ")<br>" + this.editorData[environment.DEFAULT_LANGUAGE]
        : this.translate.instant("Please enter here ") + this.store.state.common.lang;
    }
    if (!this.editorData.note) {
      this.editorData.note = this.translate.instant("Please enter here ");
    }
  }

  public updateModel( { editor }) {
    this.editorData[this.selectedTab === "note" ? this.selectedTab : this.store.state.common.lang] = editor.getData();
  }

  public save(event) {
    this.saveTutorial.emit(this.editorData);
  }

  public async ngOnInit() {
    this.config.language = this.store.state.common.lang;
    this.initialTab = this.selectedTab;
    this.checkMissingLanguage();
    this.selectedData = this.editorData[this.store.state.common.lang];
    this.changePosition(this.position);
    this.initLevelUser();
  }

  public closeTutorial() {
    // this.editorData = {};
    this.selectedTab = this.initialTab;
    this.isClosed = true;
    this.closingTutorial.emit(this.isClosed);
  }

  public changeTab(tab) {
    this.selectedTab = tab;
    this.checkMissingLanguage();
    this.selectedData = this.editorData[this.selectedTab === "note" ? this.selectedTab : this.store.state.common.lang];
  }

  public onResizeEnd(event: ResizeEvent) {
    if (this.position === "right" || this.position === "left") {
      this.style = {
        width: `${event.rectangle.width}px`,
      };
    }
    if (this.position === "top" || this.position === "bottom") {
      this.style = {
        height: `${event.rectangle.height}px`,
      };
    }
  }

  public ngOnDestroy() {
    this.editorData = {};
  }

  public initLevelUser() {
    console.log("initLevelUser", this.store.state.common.userLevels.get("SideNavLevel"));
    // eslint-disable-next-line
    if (this.store.state.common.userLevels.get("SideNavLevel") === false) {
      // eslint-disable-next-line
      this.lvl = 1;
    } else {
      // eslint-disable-next-line
      this.lvl = this.store.state.common.userLevels.get("SideNavLevel");
    }
  }

}
